import type { Fields, RootConfig } from '@puckeditor/core';

import { SURFACE_COLOR_FIELD_AI_SCHEMA, SurfaceColorField } from '../fields/widgets/surface-color-field';
import { toggleField } from '../fields/widgets/toggle-field';
import {
  seoTextField,
  SEO_DESCRIPTION_GUIDANCE_LIMIT,
  SEO_TITLE_GUIDANCE_LIMIT,
} from '../fields/widgets/seo-text-field';
import { PageRootRender } from './page-root-render';

/** Page-level appearance and chrome fields shared by every CMS editor's root config. */
const SHARED_ROOT_FIELDS: Fields<{
  backgroundColor: string;
  showHeader: boolean;
  showNavigation: boolean;
  seoTitle: string;
  seoDescription: string;
  hideFromSearch: boolean;
}> = {
  backgroundColor: {
    type: 'custom',
    label: 'Page background',
    render: SurfaceColorField,
    ai: {
      schema: SURFACE_COLOR_FIELD_AI_SCHEMA,
      instructions:
        "Set to a surface role ('none', 'surface', 'muted') so it follows merchant branding; use a hex value only when a specific brand color is required.",
    },
  },
  // Page-level Header and Navigation toggles align horizontally with their labels (ECOMM-14928).
  showHeader: toggleField('Header', undefined, undefined, { defaultOn: true }),
  showNavigation: toggleField('Navigation', undefined, undefined, { defaultOn: true }),
  // SEO fields (ECOMM-14849), ordered last to match the prototype's placement: what the page
  // is and how it's structured first, how it's found by search engines last.
  seoTitle: seoTextField('SEO title', {
    limit: SEO_TITLE_GUIDANCE_LIMIT,
    hint:
      'Shown as the clickable headline in search results and the browser tab. Falls back to the storefront name when left blank.',
    placeholder: 'e.g. Weekly Deals — Green Valley Dispensary',
    aiInstructions:
      'A concise, descriptive page title for search engine results. Aim for roughly 60 characters or fewer. Do not invent business facts, promotions, or claims not already present on the page.',
  }),
  seoDescription: seoTextField('SEO description', {
    limit: SEO_DESCRIPTION_GUIDANCE_LIMIT,
    multiline: true,
    hint:
      "Shown as the summary text under the title in search results. Search engines may ignore it and show other page text instead -- it's guidance, not a guarantee.",
    placeholder: 'A short summary of what this page offers.',
    aiInstructions:
      'A concise summary of the page for search engine results. Aim for roughly 160 characters or fewer. Do not invent business facts, promotions, or claims not already present on the page.',
  }),
  hideFromSearch: toggleField(
    'Hide from search engines',
    'Whether this page is excluded from search engines via a page-specific noindex/nofollow directive. Does not unpublish or password-protect the page, and does not guarantee immediate removal from search results already indexed.',
    "Adds page-specific noindex, nofollow directives to this page, hiding it from search engines. The page stays reachable by anyone with the link. Turning this off removes only this page's own restriction -- other applicable site-wide search rules still apply."
  ),
};

const SHARED_ROOT_DEFAULT_PROPS = {
  backgroundColor: 'surface',
  showHeader: true,
  showNavigation: true,
  seoTitle: '',
  seoDescription: '',
  hideFromSearch: false,
};

export type CreatePageRootConfigOptions = {
  /** Page-model-specific fields (e.g. standalone-page's `urlPath`), merged alongside the shared Header/Navigation fields. */
  fields?: Fields;
  /** Page-model-specific default prop values, merged alongside the shared Header/Navigation defaults. */
  defaultProps?: Record<string, unknown>;
  /** AI authoring instructions for this page model's root. Omit entirely for models that don't need AI support. */
  ai?: CmsComponentAiConfig;
};

/**
 * Builds the root Puck config shared by both CMS editors (standalone page + menu landing
 * page): the background and Header/Navigation fields and defaults, and the shared
 * PageRootRender function. Page-specific fields/defaultProps (e.g. standalone-page's
 * `urlPath`) are merged in ahead of the shared ones so they render first in the sidebar.
 */
export function createPageRootConfig({ fields, defaultProps, ai }: CreatePageRootConfigOptions = {}): RootConfig {
  return {
    fields: { ...fields, ...SHARED_ROOT_FIELDS },
    defaultProps: { ...defaultProps, ...SHARED_ROOT_DEFAULT_PROPS },
    ...(ai ? { ai } : {}),
    render: PageRootRender,
  };
}
